import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import HelpTip from "@/components/HelpTip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Megaphone, Send, ChevronLeft, ChevronRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { listClients, listSitesLite, processOutboxNow, sendBroadcastTest } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { AuDateInput, AuDateTimeInput } from "@/components/AuDateTime";
import { todayInTz } from "@/lib/datetime";

const AUDIENCE_KEYS = [
  { key: "client_admins", label: "Client admins" },
  { key: "site_admins",   label: "Site admins" },
  { key: "bookers",       label: "Bookers (any booking in window)" },
  { key: "drivers",       label: "Drivers (assigned in window)" },
];

/**
 * Administration → Broadcasts
 * Build a message to push to admins / bookers / drivers (and/or as a banner
 * on Site Info). Includes a 5-at-a-time recipient review before sending.
 */
/** Pull a banner down early / put it back up (expiry unchanged). */
function BannerSwitch({ b, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!b.channels?.banner) return <span className="text-xs text-slate-400">—</span>;
  const expired = b.banner_expires_at && new Date(b.banner_expires_at).getTime() <= Date.now();
  if (expired) return <span className="text-xs text-slate-400" data-testid={`broadcast-banner-expired-${b.id}`}>Expired</span>;
  const on = b.banner_enabled !== false;
  const flip = async (next) => {
    setBusy(true);
    const { error } = await supabase.from("lbs_broadcasts").update({ banner_enabled: next }).eq("id", b.id);
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(next ? "Banner is live again." : "Banner taken down.");
    window.dispatchEvent(new Event("lbs:broadcasts-changed"));
    onChanged();
  };
  return (
    <div className="flex items-center gap-2">
      <Switch checked={on} disabled={busy} onCheckedChange={flip} data-testid={`broadcast-banner-switch-${b.id}`} />
      <span className={`text-xs ${on ? "text-emerald-700" : "text-slate-500"}`}>{on ? "Live" : "Off"}</span>
    </div>
  );
}

export default function Broadcasts() {
  const { viewAsClientId } = useViewAsClient();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [composer, setComposer] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("lbs_broadcasts").select("*")
        .order("created_at", { ascending: false }).limit(50);
      if (!cancelled) setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [tick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite(viewAsClientId || null);
      if (!cancelled) setSites(data || []);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  return (
    <div className="space-y-4" data-testid="broadcasts-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Megaphone className="h-6 w-6" /> Broadcasts
          </h1>
          <p className="text-sm text-slate-500">
            Push a message to everyone touching bookings in a chosen window —
            via email, an on-site banner, or both.
          </p>
        </div>
        <Button onClick={() => setComposer({})} data-testid="broadcast-new">
          <Megaphone className="h-4 w-4 mr-1.5" /> New broadcast
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Recent broadcasts</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Channels</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Banner</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-400">
                  No broadcasts yet.
                </TableCell></TableRow>
              ) : rows.map((b) => (
                <TableRow key={b.id} data-testid={`broadcast-row-${b.id}`}>
                  <TableCell className="text-sm font-medium">{b.title}</TableCell>
                  <TableCell className="text-sm">{sites.find((s) => s.id === b.site_id)?.name || "All sites"}</TableCell>
                  <TableCell className="text-xs tabular-nums">{b.window_start} → {b.window_end}</TableCell>
                  <TableCell className="text-xs">
                    {b.channels?.email  && <Badge variant="secondary" className="mr-1">email</Badge>}
                    {b.channels?.banner && <Badge variant="secondary" className="bg-amber-100 text-amber-800">banner</Badge>}
                  </TableCell>
                  <TableCell className="text-xs">{b.sent_at ? new Date(b.sent_at).toLocaleString("en-AU", { hourCycle: "h23" }) : "—"}</TableCell>
                  <TableCell><BannerSwitch b={b} onChanged={() => setTick((k) => k + 1)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {composer !== null && (
        <BroadcastComposer
          sites={sites}
          clientId={viewAsClientId}
          userId={user?.id}
          userEmail={user?.email}
          onClose={() => setComposer(null)}
          onSent={() => { setComposer(null); setTick((k) => k + 1); }}
        />
      )}
    </div>
  );
}

function BroadcastComposer({ sites, clientId, userId, userEmail, onClose, onSent }) {
  const [step, setStep] = useState("compose"); // compose | review
  const [clients, setClients] = useState([]);
  const [v, setV] = useState({
    title: "", body_markdown: "",
    client_id: clientId || "",
    site_id: "all",
    window_start: today(), window_end: addDays(today(), 7),
    audience: { client_admins: true, site_admins: true, bookers: true, drivers: true },
    channels: { email: true, banner: false },
    banner_expires_at: addDays(today(), 7) + "T17:00:00",
  });
  const [siteOpts, setSiteOpts] = useState(sites);
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected]   = useState(new Set());
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const audienceFlip = (k) => setV((s) => ({ ...s, audience: { ...s.audience, [k]: !s.audience[k] } }));

  // Load clients (super-admin sees all; client-admin sees their own — RLS).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listClients();
      if (!cancelled) setClients(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Refresh site dropdown whenever the chosen client changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!v.client_id) { if (!cancelled) setSiteOpts([]); return; }
      const { data } = await listSitesLite(v.client_id);
      if (cancelled) return;
      setSiteOpts(data || []);
      // Reset site selection if the current one no longer belongs to this client
      setV((s) => (s.site_id !== "all" && !(data || []).some((x) => x.id === s.site_id) ? { ...s, site_id: "all" } : s));
    })();
    return () => { cancelled = true; };
  }, [v.client_id]);

  const goReview = async () => {
    if (!v.client_id) { toast.error("Pick a client."); return; }
    if (!v.title.trim() || !v.body_markdown.trim()) { toast.error("Title and message body are required."); return; }
    if (v.channels.banner && !v.banner_expires_at) { toast.error("Banner expiry is required when banner channel is enabled."); return; }
    setBusy(true);
    const insertPayload = {
      client_id: v.client_id, site_id: v.site_id === "all" ? null : v.site_id,
      title: v.title, body_markdown: v.body_markdown,
      audience: v.audience, channels: v.channels,
      window_start: v.window_start, window_end: v.window_end,
      banner_expires_at: v.channels.banner ? v.banner_expires_at : null,
      created_by_user_id: userId,
    };
    const { data: inserted, error } = await supabase.from("lbs_broadcasts").insert(insertPayload).select().single();
    if (error) { setBusy(false); toast.error(error.message); return; }
    const { data: recs, error: rerr } = await supabase.rpc("lbs_resolve_broadcast_recipients", { p_broadcast_id: inserted.id });
    setBusy(false);
    if (rerr) { toast.error(rerr.message); return; }
    setRecipients(recs || []);
    setSelected(new Set((recs || []).map((r) => r.user_id)));
    setV((s) => ({ ...s, id: inserted.id }));
    setStep("review");
  };

  const send = async () => {
    setBusy(true);
    let enqueued = 0;

    if (v.channels.email) {
      // Server-side RPC: atomic auth + enqueue + sent_at stamp.
      // Browser-side outbox inserts are blocked by RLS (no INSERT policy on lbs_email_outbox).
      const picks = (recipients || [])
        .filter((r) => selected.has(r.user_id))
        .map((r) => r.user_id);
      const html = broadcastHtml(v);
      const text = broadcastText(v);
      const { data, error } = await supabase.rpc("lbs_send_broadcast", {
        p_broadcast_id: v.id,
        p_user_ids: picks,
        p_subject: v.title,
        p_body_html: html,
        p_body_text: text,
      });
      if (error) {
        setBusy(false);
        toast.error(`Send failed: ${error.message}`);
        return;
      }
      enqueued = Number(data ?? 0);
    } else {
      // Banner-only — just stamp sent_at
      const { error } = await supabase.from("lbs_broadcasts")
        .update({ sent_at: new Date().toISOString() }).eq("id", v.id).select();
      if (error) {
        setBusy(false);
        toast.error(`Send failed: ${error.message}`);
        return;
      }
    }
    // Kick the drainer (paced)
    processOutboxNow(50).catch(() => {});
    setBusy(false);
    toast.success(`Broadcast sent${v.channels.email ? ` (${enqueued} emails queued)` : ""}.`);
    onSent?.();
  };

  // Pagination of recipients
  const PAGE = 5;
  const total = recipients.length;
  const start = page * PAGE;
  const slice = recipients.slice(start, start + PAGE);
  const allOnPage = useMemo(() => slice.every((r) => selected.has(r.user_id)), [slice, selected]);

  const toggle = (uid) => setSelected((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    if (allOnPage) slice.forEach((r) => n.delete(r.user_id));
    else slice.forEach((r) => n.add(r.user_id));
    return n;
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="broadcast-composer">
        <DialogHeader>
          <DialogTitle>{step === "compose" ? "New broadcast" : `Review recipients (${total})`}</DialogTitle>
        </DialogHeader>

        {step === "compose" ? (
          <div className="space-y-3">
            <Field label="Title *">
              <Input value={v.title} onChange={(e) => set("title", e.target.value)} data-testid="bc-title" />
            </Field>
            <Field label="Message (markdown supported) *">
              <Textarea rows={5} value={v.body_markdown} onChange={(e) => set("body_markdown", e.target.value)} data-testid="bc-body" />
            </Field>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Client *">
                <Select value={v.client_id || ""} onValueChange={(x) => set("client_id", x)}>
                  <SelectTrigger data-testid="bc-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Site (optional)">
                <Select value={v.site_id} onValueChange={(x) => set("site_id", x)} disabled={!v.client_id}>
                  <SelectTrigger data-testid="bc-site"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sites</SelectItem>
                    {siteOpts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Window start"><AuDateInput value={v.window_start} onChange={(e) => set("window_start", e.target.value)} data-testid="bc-window-start" /></Field>
              <Field label="Window end"><AuDateInput value={v.window_end} onChange={(e) => set("window_end", e.target.value)} data-testid="bc-window-end" /></Field>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Audience</Label>
                <HelpTip id="bc-audience">
                  Pick every group that should receive the broadcast. Client admins and site admins
                  see it in-app and (if email is on) get an email. Bookers and drivers only receive
                  channels they've enabled in their notification preferences.
                </HelpTip>
              </div>
              <div className="flex flex-wrap gap-3 mt-1.5">
                {AUDIENCE_KEYS.map(({ key, label }) => (
                  <label key={key} className="text-sm flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={!!v.audience[key]} onChange={() => audienceFlip(key)} data-testid={`bc-aud-${key}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Channels</Label>
                <HelpTip id="bc-channels">
                  <div>
                    <p className="mb-1"><strong>Email</strong> sends via the Outbox — respect the Emergency Broadcast rule (banner-only) for out-of-hours weather alerts.</p>
                    <p><strong>Site Info banner</strong> pins the message to the top of the driver-facing Site Info page until the expiry you pick.</p>
                  </div>
                </HelpTip>
              </div>
              <div className="flex flex-wrap gap-4 mt-1.5">
                <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={v.channels.email}
                  onChange={() => set("channels", { ...v.channels, email: !v.channels.email })} data-testid="bc-ch-email" /> Email</label>
                <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={v.channels.banner}
                  onChange={() => set("channels", { ...v.channels, banner: !v.channels.banner })} data-testid="bc-ch-banner" /> Site Info banner</label>
                {v.channels.banner && (
                  <div className="text-sm flex items-center gap-1.5">
                    expires
                    <AuDateTimeInput value={v.banner_expires_at}
                           onChange={(e) => set("banner_expires_at", e.target.value)} data-testid="bc-banner-expiry" />
                  </div>
                )}
              </div>
            </div>
            <TestSend v={v} defaultEmail={userEmail} />
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              {total} recipient{total === 1 ? "" : "s"} resolved. Step through 5 at a time, untick anyone
              who should be skipped, then send. Emails are throttled by the outbox drainer to avoid spam flags.
            </p>
            <div className="rounded border">
              <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={allOnPage} onChange={togglePage} data-testid="bc-page-toggle" />
                  Select this page
                </label>
                <span className="text-slate-500">{start + 1}–{Math.min(start + PAGE, total)} of {total}</span>
              </div>
              <ul className="divide-y">
                {slice.map((r) => (
                  <li key={r.user_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`bc-recipient-${r.user_id}`}>
                    <input type="checkbox" checked={selected.has(r.user_id)} onChange={() => toggle(r.user_id)} />
                    <Mail className="h-3.5 w-3.5 text-slate-400" />
                    <span className="flex-1 truncate">{r.email}</span>
                    <Badge variant="secondary" className="text-[10px]">{r.role}</Badge>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between px-3 py-2 border-t bg-slate-50">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="bc-prev">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                </Button>
                <span className="text-xs text-slate-500">{selected.size} selected</span>
                <Button size="sm" variant="outline" disabled={start + PAGE >= total} onClick={() => setPage((p) => p + 1)} data-testid="bc-next">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "compose" ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={goReview} disabled={busy} data-testid="bc-to-review">Next: review recipients</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep("compose")} disabled={busy}>Back</Button>
              <Button onClick={send} disabled={busy || (v.channels.email && selected.size === 0)} data-testid="bc-send">
                <Send className="h-4 w-4 mr-1.5" />
                {busy ? "Sending…" : `Send${v.channels.email ? ` (${selected.size})` : ""}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
        {hint ? <HelpTip>{hint}</HelpTip> : null}
      </div>
      {children}
    </div>
  );
}
function broadcastHtml(v) { return `<h2 style="margin:0 0 8px;">${escapeHtml(v.title)}</h2>${markdownInlineToHtml(v.body_markdown)}`; }
function broadcastText(v) { return `${v.title}\n\n${v.body_markdown}`; }

/** Send one [TEST] copy of the draft to a single address — nobody else receives it. */
function TestSend({ v, defaultEmail }) {
  const [to, setTo] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!v.client_id) { toast.error("Pick a client first."); return; }
    if (!v.title.trim() || !v.body_markdown.trim()) { toast.error("Add a title and message first."); return; }
    if (!to.includes("@")) { toast.error("Enter the email address for the test."); return; }
    setBusy(true);
    try {
      await sendBroadcastTest({ client_id: v.client_id, to_email: to.trim(), subject: v.title,
        body_html: broadcastHtml(v), body_text: broadcastText(v) });
      toast.success(`Test sent to ${to.trim()} only.`);
    } catch (e) {
      toast.error(e?.message || "Test send failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-dashed bg-slate-50 p-3" data-testid="bc-test-panel">
      <Label className="text-xs uppercase tracking-wider text-slate-500">Send a test first</Label>
      <p className="text-xs text-slate-500 mt-0.5">Only this address gets a copy (subject starts with [TEST]). Nothing is sent to carriers or drivers.</p>
      <div className="flex gap-2 mt-2">
        <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" data-testid="bc-test-email" />
        <Button type="button" variant="outline" onClick={go} disabled={busy} data-testid="bc-test-send">
          <Mail className="h-4 w-4 mr-1.5" />{busy ? "Sending…" : "Send test to me"}
        </Button>
      </div>
    </div>
  );
}
function today() { return todayInTz("Australia/Melbourne"); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); }
function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])); }
function markdownInlineToHtml(md) {
  // Very small subset for the broadcast email body. Full markdown handled
  // client-side in the banner; for email we just newline-to-<br>.
  return `<div style="white-space:pre-wrap;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;color:#0f172a;font-size:14px;line-height:1.5;">${escapeHtml(md)}</div>`;
}
